import { space } from '@sunwebgroup/ui-components'
import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { css, styled } from 'styled-components'
import { EmblaCarouselContext } from './useCarouselItemsInView'

export const Container = styled.div`
  width: 100%;
  overflow: hidden;
`
interface CarouselProps extends React.PropsWithChildren {
  isFullWidth?: boolean
}

const ItemsWrapper = styled.div<{ $isFullWidth: boolean }>`
  display: flex;
  gap: ${space('primitives_space_8')};

  ${({ $isFullWidth }) => $isFullWidth && css`
    & > :first-child {
      margin-left: ${space('primitives_space_16')};
    }
    & > :last-child {
      margin-right: ${space('primitives_space_16')};
    }
  `}
`

const OPTIONS: EmblaOptionsType = { dragFree: true, loop: false, startIndex: 0, align: 'start' }

export function Carousel({ children, isFullWidth = false }: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(OPTIONS)
  const [lazyLoadedSlides, setLazyLoadedSlides] = useState<HTMLElement[]>([])

  const inViewHandler = useCallback(() => {
    if (!emblaApi) {
      return
    }

    const slides = emblaApi.slideNodes()
    const slidesInView = emblaApi.slidesInView()
    const items = slidesInView.map(slide => slides[slide])
    const newLazyLoadedSlides = Array.from(new Set([...lazyLoadedSlides, ...items]))

    if (slides.length === newLazyLoadedSlides.length) {
      emblaApi.off('slidesInView', inViewHandler)
    }

    setLazyLoadedSlides(newLazyLoadedSlides)
  }, [emblaApi, lazyLoadedSlides])

  useEffect(() => {
    if (!emblaApi || emblaApi.slideNodes().length === lazyLoadedSlides.length) {
      return
    }

    emblaApi.on('slidesInView', inViewHandler)

    return () => {
      emblaApi.off('slidesInView', inViewHandler)
    }
  }, [emblaApi, inViewHandler, lazyLoadedSlides.length])

  return (
    <EmblaCarouselContext.Provider value={lazyLoadedSlides}>
      <Container>
        <div ref={emblaRef}>
          <ItemsWrapper $isFullWidth={isFullWidth}>{children}</ItemsWrapper>
        </div>
      </Container>
    </EmblaCarouselContext.Provider>
  )
}
